import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";

import {
  getMyWatchlist,
  addStudentToMyWatchlist,
  removeStudentFromMyWatchlist,
  reorderMyWatchlistStudent,
  type WatchlistItem,
  type ReorderWatchlistRequest,
  type ReorderWatchlistResponse,
  type AddToWatchlistResponse,
} from "./api";
import { usePortalModeStore } from "@stores/usePortalModeStore";
import { useStaffOverrideStore } from "@stores/useStaffOverrideStore";

const isStudentImpersonation = () => {
  const { override, overrideType } = useStaffOverrideStore.getState();
  return Boolean(override && overrideType === "student");
};

/**
 * Query options
 */
export const myWatchlistQueryOptions = () => {
  const { override, overrideType } = useStaffOverrideStore.getState();
  const impersonatingStudent = Boolean(override && overrideType === "student");

  return queryOptions({
    queryKey: ["watchlist", "my", { override }],
    queryFn: () => isStudentImpersonation() ? Promise.resolve([] as WatchlistItem[]) : getMyWatchlist(),
    staleTime: 60_000,
    enabled: usePortalModeStore.getState().hostMode === "staff" && !impersonatingStudent,
  });
};

export function useMyWatchlistQuery() {
  return useQuery(myWatchlistQueryOptions());
}

export function useMyWatchlistSuspense() {
  return useSuspenseQuery(myWatchlistQueryOptions());
}

/**
 * Mutations
 */
export function useAddToWatchlistMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (studentNumber: string): Promise<AddToWatchlistResponse> =>
      addStudentToMyWatchlist(studentNumber),

    onMutate: async (studentNumber) => {
      const queryKey = myWatchlistQueryOptions().queryKey;

      await queryClient.cancelQueries({ queryKey });

      const prev = queryClient.getQueryData<WatchlistItem[]>(queryKey);

      queryClient.setQueryData<WatchlistItem[]>(queryKey, (old) => {
        if (old?.some((item) => item.studentNumber === studentNumber)) {
          return old;
        }

        return [
          ...(old ?? []),
          {
            staffInitials: "",
            studentNumber,
            sortKey: `optimistic-${studentNumber}`,
            attendance: { presentLength: 0, totalLength: 1 },
            punctuality: { punctualLength: 0, totalLength: 1 },
          },
        ];
      });

      return { prev };
    },

    onError: (error, _studentNumber, context) => {
      console.error("Error adding student to watchlist, rolling back:", error);
      if (!context) return;

      queryClient.setQueryData(myWatchlistQueryOptions().queryKey, context.prev);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: myWatchlistQueryOptions().queryKey });
    },
  });
}

export function useAddManyToWatchlistMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (studentNumbers: string[]): Promise<AddToWatchlistResponse[]> =>
      Promise.all(studentNumbers.map((studentNumber) => addStudentToMyWatchlist(studentNumber))),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: myWatchlistQueryOptions().queryKey });
    },
  });
}

export function useRemoveFromWatchlistMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (studentNumber: string): Promise<void> => removeStudentFromMyWatchlist(studentNumber),

        onMutate: async (studentNumber) => {
            // Optimistically remove from cache
            await queryClient.cancelQueries({ queryKey: myWatchlistQueryOptions().queryKey });

            const prev = queryClient.getQueryData<WatchlistItem[]>(
                myWatchlistQueryOptions().queryKey,
            );

            queryClient.setQueryData<WatchlistItem[]>(
                myWatchlistQueryOptions().queryKey,
                (old) => (old ?? []).filter((x) => x.studentNumber !== studentNumber),
            );

            return { prev };
        },

        onError: (err, _studentNumber, context) => {
            console.error("Error removing from watchlist, rolling back:", err);
            if (!context) return;

            queryClient.setQueryData(myWatchlistQueryOptions().queryKey, context.prev);
        },

        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: myWatchlistQueryOptions().queryKey });
        },
  });
}

export function useRemoveManyFromWatchlistMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (studentNumbers: string[]): Promise<void[]> =>
      Promise.all(studentNumbers.map((studentNumber) => removeStudentFromMyWatchlist(studentNumber))),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: myWatchlistQueryOptions().queryKey });
    },
  });
}

export function useReorderWatchlistMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: ReorderWatchlistRequest,
    ): Promise<ReorderWatchlistResponse> => reorderMyWatchlistStudent(payload),

    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: myWatchlistQueryOptions().queryKey });

      const prev = queryClient.getQueryData<WatchlistItem[]>(
        myWatchlistQueryOptions().queryKey,
      );

      // Optimistic reorder: rearrange by neighbours in the cached list.
      queryClient.setQueryData<WatchlistItem[]>(
        myWatchlistQueryOptions().queryKey,
        (old) => {
          const list = [...(old ?? [])];
          const fromIdx = list.findIndex((x) => x.studentNumber === payload.studentNumber);
          if (fromIdx === -1) return list;

          const [moved] = list.splice(fromIdx, 1);

          // Determine insertion index based on neighbours in the new list.
          // If beforeStudentNumber is set, insert after it.
          // Else if afterStudentNumber is set, insert before it.
          // Else: keep at start.
          let toIdx = 0;

          if (payload.beforeStudentNumber) {
            const beforeIdx = list.findIndex(
              (x) => x.studentNumber === payload.beforeStudentNumber,
            );
            toIdx = beforeIdx === -1 ? list.length : beforeIdx + 1;
          } else if (payload.afterStudentNumber) {
            const afterIdx = list.findIndex(
              (x) => x.studentNumber === payload.afterStudentNumber,
            );
            toIdx = afterIdx === -1 ? 0 : afterIdx;
          } else {
            toIdx = 0;
          }

          list.splice(toIdx, 0, moved);

          // Keep sortKey as-is optimistically; server will return the real sortKey.
          return list;
        },
      );

      return { prev };
    },

    onError: (err, _payload, context) => {
      console.error("Error reordering watchlist, rolling back:", err);
      if (!context) return;

      queryClient.setQueryData(myWatchlistQueryOptions().queryKey, context.prev);
    },

    onSuccess: (data) => {
      // Patch the moved item's sortKey in cache with the real one from the server
      queryClient.setQueryData<WatchlistItem[]>(
        myWatchlistQueryOptions().queryKey,
        (old) =>
          (old ?? []).map((x) =>
            x.studentNumber === data.studentNumber
              ? { ...x, sortKey: data.sortKey }
              : x,
          ),
      );
    },

    onSettled: () => {
      // Always reconcile ordering from server (covers rebalance + concurrency)
      queryClient.invalidateQueries({ queryKey: myWatchlistQueryOptions().queryKey });
    },
  });
}
